// TopProgressBar.vue
<template>
  <div
    class="top-progress-bar"
    :style="barStyle"
    :class="{ 'visible': isVisible }"
  ></div>
</template>

// TopProgressBar.vue
<script setup lang="ts">
import { ref, computed, onBeforeUnmount } from 'vue'; // Import onBeforeUnmount

// --- Props ---
interface Props {
  color?: string;
  height?: number;
  speed?: number; // Transition speed for width/opacity (ms)
  finishDelay?: number; // Delay before hiding after finish() (ms)
}

const props = withDefaults(defineProps<Props>(), {
  color: '#70C381',
  height: 7,
  speed: 200,
  finishDelay: 300,
});

// --- Constants ---
const TRICKLE_DURATION = 10000; // Target duration for auto-increase (ms)
const TRICKLE_TARGET_PERCENT = 95; // Target percentage for auto-increase

// --- State ---
const percent = ref<number>(0);
const isVisible = ref<boolean>(false);
const finishTimeoutId = ref<number | null>(null);
const trickleRafId = ref<number | null>(null); // ID for requestAnimationFrame
const trickleStartTime = ref<number | null>(null); // Timestamp when trickle started

// --- Computed Style ---
const barStyle = computed(() => ({
  position: 'absolute',
  top: '0',
  left: '0',
  height: `${props.height}px`,
  width: `${percent.value}%`,
  backgroundColor: props.color,
  opacity: isVisible.value ? 1 : 0,
  transition: `width ${props.speed}ms linear, opacity ${props.speed}ms ease`, // Use linear for width during trickle
  zIndex: 9999,
}));

// --- Internal Functions ---
const clearFinishTimeout = () => {
  if (finishTimeoutId.value !== null) {
    clearTimeout(finishTimeoutId.value);
    finishTimeoutId.value = null;
  }
};

const cancelTrickle = () => {
  if (trickleRafId.value !== null) {
    cancelAnimationFrame(trickleRafId.value);
    trickleRafId.value = null;
  }
  trickleStartTime.value = null;
};

// The main trickle loop using requestAnimationFrame
const doTrickle = () => {
  // Ensure we are still supposed to be trickling
  if (!trickleStartTime.value || trickleRafId.value === null) return;

  const elapsed = Date.now() - trickleStartTime.value;
  const timeProgress = Math.min(elapsed / TRICKLE_DURATION, 1); // 0 to 1

  // Calculate base target using an ease-out cubic function (starts faster)
  // We start from the current percentage or a minimum boost
  const startingPercent = Math.max(percent.value, 1); // Ensure we don't calculate from 0 after initial boost
  let targetPercent = startingPercent + (TRICKLE_TARGET_PERCENT - startingPercent) * (1 - Math.pow(1 - timeProgress, 3));

  // Add a small, slightly increasing random amount to make it uneven
  targetPercent += (Math.random() * timeProgress * 0.5); // Max ~0.25% random boost near the end

  // Ensure we only increase and stay below the trickle target
  const newPercent = Math.max(percent.value, Math.min(targetPercent, TRICKLE_TARGET_PERCENT));

  // Update the percentage if it has increased
  if (newPercent > percent.value) {
    percent.value = newPercent;
  }

  // Continue the loop if time is not up and trickle hasn't been cancelled
  if (timeProgress < 1 && trickleRafId.value !== null) {
    trickleRafId.value = requestAnimationFrame(doTrickle);
  } else if (timeProgress >= 1) {
    // Reached target duration, stop the automatic trickle
    cancelTrickle();
  }
};


// --- Exposed Methods ---
const start = () => {
  clearFinishTimeout();
  cancelTrickle(); // Stop any previous trickle
  percent.value = 0;
  isVisible.value = true;

  // Use rAF to apply initial boost and start trickle on next frame
  requestAnimationFrame(() => {
    // Check if still visible and at 0% in case finish() was called immediately
    if (isVisible.value && percent.value === 0) {
      // Apply a small initial percentage boost
      percent.value = Math.random() * 5 + 1; // e.g., 1% to 6%
    }

    // Record start time and schedule the first trickle animation frame
    trickleStartTime.value = Date.now();
    trickleRafId.value = requestAnimationFrame(doTrickle);
  });
};

const set = (amount: number) => {
  clearFinishTimeout();
  cancelTrickle(); // Stop trickle on manual set

  const clampedAmount = Math.max(0, Math.min(100, amount));
  percent.value = clampedAmount;
  isVisible.value = clampedAmount > 0; // Show if > 0

  // If set to 100 or more, treat it as finishing
  if (clampedAmount >= 100) {
    finish(); // Use finish logic to hide smoothly
  }
};

const increase = (amount: number) => {
  // Only increase if not already finished/finishing
  if (percent.value >= 100 || finishTimeoutId.value !== null) return;

  cancelTrickle(); // Stop auto-trickle if manually increasing
  const current = percent.value;
  const newAmount = Math.min(current + amount, 100); // Clamp at 100
  set(newAmount); // Use set to handle clamping, visibility, and potential finish
};

const finish = () => {
  // Prevent multiple finish calls overlapping
  if (finishTimeoutId.value !== null) return;

  cancelTrickle(); // Ensure trickle is stopped
  percent.value = 100; // Go immediately to 100%
  isVisible.value = true; // Ensure it's visible for the final state

  // Set a timeout to hide the bar after a short delay
  finishTimeoutId.value = window.setTimeout(() => {
    isVisible.value = false;
    // Reset percent *after* the fade-out transition completes
    setTimeout(() => {
      // Only reset if it wasn't started again in the meantime
      if (!isVisible.value) {
        percent.value = 0;
      }
      finishTimeoutId.value = null; // Clear the flag once fully hidden and reset
    }, props.speed); // Wait for opacity transition duration
  }, props.finishDelay); // User-defined delay before starting fade out
};

// --- Lifecycle Hook ---
// Cleanup timers and animation frames when the component is unmounted
onBeforeUnmount(() => {
  clearFinishTimeout();
  cancelTrickle();
});

// --- Expose Methods ---
defineExpose({
  start,
  finish,
  set,
  increase,
});
</script>

<style scoped>
.top-progress-bar {
  /* pointer-events: none; */ /* Allows clicks to pass through if needed */
  will-change: width, opacity; /* Hint browser for optimization */
}
</style>
